import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@shared/lib/supabase';
import { Bell, CheckCheck, ShoppingCart, CheckCircle, XCircle, RotateCcw, DollarSign } from 'lucide-react';

interface Notification {
  id: string;
  merchant_id: string;
  type: string;
  title: string;
  body: string;
  metadata: Record<string, unknown>;
  read: boolean;
  created_at: string;
}

const TYPE_CONFIG: Record<string, { icon: typeof Bell; color: string; bg: string }> = {
  new_order: { icon: ShoppingCart, color: 'text-blue-400', bg: 'bg-blue-500/10' },
  order_completed: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
  order_failed: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10' },
  order_refunded: { icon: RotateCcw, color: 'text-amber-400', bg: 'bg-amber-500/10' },
  payout: { icon: DollarSign, color: 'text-teal-400', bg: 'bg-teal-500/10' },
};

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function NotificationBell({ merchantId }: { merchantId: string }) {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const unreadCount = notifications.filter((n) => !n.read).length;

  const fetchNotifications = useCallback(async () => {
    const { data } = await supabase
      .from('notifications')
      .select('*')
      .eq('merchant_id', merchantId)
      .order('created_at', { ascending: false })
      .limit(30);
    if (data) setNotifications(data as unknown as Notification[]);
    setLoading(false);
  }, [merchantId]);

  useEffect(() => {
    fetchNotifications();

    const channel = supabase
      .channel('notifications-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `merchant_id=eq.${merchantId}`,
        },
        (payload) => {
          setNotifications((prev) => [payload.new as Notification, ...prev].slice(0, 30));
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [merchantId, fetchNotifications]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (id: string) => {
    await supabase.from('notifications').update({ read: true }).eq('id', id);
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };

  const markAllRead = async () => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n.id);
    if (unreadIds.length === 0) return;
    await supabase.from('notifications').update({ read: true }).in('id', unreadIds);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  const handleClick = (n: Notification) => {
    markAsRead(n.id);
    const orderId = n.metadata?.order_id as string | undefined;
    if (orderId) {
      const isStripe = n.metadata?.provider === 'stripe';
      navigate(isStripe ? `/dashboard/stripe/orders` : `/dashboard/crypto/orders`);
    }
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded transition-colors"
        style={{ color: 'var(--dash-text-muted)' }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--dash-text-secondary)'; e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--dash-text-muted)'; e.currentTarget.style.background = ''; }}
      >
        <Bell className="w-4 h-4" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 min-w-[16px] h-4 flex items-center justify-center bg-blue-500 text-white text-[9px] font-mono font-bold rounded-full px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 rounded-lg shadow-2xl z-50 overflow-hidden"
          style={{ background: 'var(--dash-overlay)', border: '1px solid var(--dash-border-solid)' }}
        >
          <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: '1px solid var(--dash-border)' }}>
            <span className="font-sans text-[11px] font-medium" style={{ color: 'var(--dash-text-secondary)' }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllRead}
                className="flex items-center gap-1 text-[10px] font-mono transition-colors"
                style={{ color: 'var(--dash-text-muted)' }}
              >
                <CheckCheck className="w-2.5 h-2.5" />
                Mark all read
              </button>
            )}
          </div>

          <div className="max-h-[260px] overflow-y-auto overscroll-contain">
            {loading && (
              <div className="py-6 text-center">
                <span className="font-mono text-[10px] animate-pulse" style={{ color: 'var(--dash-text-faint)' }}>Loading...</span>
              </div>
            )}

            {!loading && notifications.length === 0 && (
              <div className="py-6 text-center">
                <Bell className="w-4 h-4 mx-auto mb-1.5" style={{ color: 'var(--dash-text-dim)' }} />
                <span className="font-sans text-[11px]" style={{ color: 'var(--dash-text-faint)' }}>No notifications yet</span>
              </div>
            )}

            {!loading &&
              notifications.map((n) => {
                const config = TYPE_CONFIG[n.type] || TYPE_CONFIG.new_order;
                const Icon = config.icon;
                return (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className="w-full flex items-start gap-2 px-3 py-2 text-left transition-colors"
                    style={{
                      background: !n.read ? 'var(--dash-hover-bg)' : undefined,
                      borderBottom: '1px solid var(--dash-border-light)',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = !n.read ? 'var(--dash-hover-bg)' : ''; }}
                  >
                    <div className={`mt-px w-5 h-5 rounded ${config.bg} flex items-center justify-center shrink-0`}>
                      <Icon className={`w-3 h-3 ${config.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span
                          className="font-sans text-[11px] leading-tight"
                          style={{ color: !n.read ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)', fontWeight: !n.read ? 500 : 400 }}
                        >
                          {n.title}
                        </span>
                        {!n.read && <span className="w-1 h-1 rounded-full bg-blue-500 shrink-0" />}
                      </div>
                      <p className="font-sans text-[10px] mt-0.5 truncate" style={{ color: 'var(--dash-text-muted)' }}>{n.body}</p>
                      <span className="font-mono text-[9px] mt-0.5 block" style={{ color: 'var(--dash-text-faint)' }}>{timeAgo(n.created_at)}</span>
                    </div>
                  </button>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}
